import React, { isValidElement } from 'react';
import styled, { css } from 'styled-components/macro';
import { variant } from 'styled-system';

import Button from '../Button';
import Icon, { cssToggleIconState } from '../Icon';
import {
  trackingId,
  useFocusVisibleIE,
  themeGetAsRem,
  themeGetResponsive,
  useResponsiveCtx,
} from '@blueprint/ui';

/**
 * Generate theme based
 * button icon
 */
const ButtonIcon = styled(Icon)`
  ${({ children }) =>
    children && variant({ prop: 'direction', scale: 'buttons.icon.direction' })}
  ${variant({ scale: 'buttons.icon.variantMapping' })}
`;

const IconButtonWrapper = styled(Button)`
  ${({ disabled, ariaDisabled, icon }) =>
    !ariaDisabled && !disabled && cssToggleIconState(icon, ['hover'])};
  ${({ size = 'large', pureIcon }) =>
    pureIcon && themeGetAsRem(`buttons.pureIcon.sizeMapping.${size}`)};
  ${({ vMedia, hMedia, size = 'large', pureIcon }) => css`
    ${pureIcon &&
    themeGetResponsive({
      vMedia,
      hMedia,
      vMediaStyles: css`
        ${themeGetAsRem('buttons.pureIcon.sizeMapping.small')};
      `,
    })};
    ${hMedia &&
    pureIcon &&
    themeGetAsRem(`buttons.hBreakpoints.pureIcon.sizeMapping.${size}`)};
  `};
`;

/**
 * IconButton
 */
const IconButton = ({
  variant = 'light.primary',
  direction = 'left',
  icon,
  iconProps,
  children,
  ...rest
}) => {
  const IconComponent = isValidElement(icon) ? (
    icon
  ) : (
    <ButtonIcon
      component="span"
      variant={variant}
      svgProps={{ color: 'inherit' }}
      name={icon}
      direction={direction}
      children={children}
      {...iconProps}
    />
  );
  const { vMedia, hMedia } = useResponsiveCtx();
  const focusVisibleIE = useFocusVisibleIE(rest);

  // assign the direction
  const startWithIconOnRight = direction === 'right';
  const startWithIconOnLeft = direction === 'left';

  return (
    <IconButtonWrapper
      variant={variant}
      data-trackid={trackingId('IconButton')}
      data-testid="IconButton"
      icon={icon}
      pureIcon={!children}
      {...rest}
      {...focusVisibleIE}
      vMedia={vMedia}
      hMedia={hMedia}
    >
      {startWithIconOnLeft && IconComponent}
      {children && <span>{children}</span>}
      {startWithIconOnRight && IconComponent}
    </IconButtonWrapper>
  );
};

IconButton.displayName = 'IconButton';

export default IconButton;
